import { Component } from '@theme/component';

const CLOSE_DELAY = 120;
const SCROLL_TOP_THRESHOLD = 16;
const SCROLL_TRIGGER_DISTANCE = 48;

/**
 * Custom element that wires up the APC header: distributes merchant-placed
 * megamenu panel blocks into their matching megamenu content slots, manages
 * megamenu open/close state, and collapses the menu row on scroll.
 *
 * Megamenu panel blocks live inside the navbar block under `[data-megamenu-source]`,
 * so they can be edited as nested blocks while their rendered content is moved
 * into the visible megamenu panel slots at runtime.
 *
 * @extends {Component<{}>}
 */
class ApcHeader extends Component {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {number | null} */
  #closeTimer = null;
  /** @type {string | null} */
  #activeHandle = null;
  #scrollLastY = 0;
  #scrollDownAccum = 0;
  #scrollUpAccum = 0;
  #scrollTicking = false;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#distributeMegamenuBlocks();
    this.#setupScrollCollapse(signal);
    this.#setupMegamenu(signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }

  /**
   * Moves children from one element to another.
   * @param {Element} from
   * @param {Element} to
   */
  #moveChildren(from, to) {
    while (from.firstElementChild) {
      to.appendChild(from.firstElementChild);
    }
  }

  /**
   * Distributes merchant-placed megamenu panel block content into the
   * matching megamenu content slots emitted by the navbar block.
   *
   * The navbar block renders panel blocks inside `[data-megamenu-source]`
   * (hidden) and renders empty panel slots with `[data-megamenu-content]`
   * for each menu link. This routes each panel block's children to its
   * target slot(s) by `apply_to` and `menu_index`.
   */
  #distributeMegamenuBlocks() {
    const sources = this.querySelectorAll('[data-megamenu-source]');
    const panelContents = this.querySelectorAll('[data-megamenu-content]');
    if (!sources.length || !panelContents.length) return;

    sources.forEach((source) => {
      const blocks = Array.from(source.querySelectorAll(':scope > .shopify-block > [data-megamenu-block]'));

      blocks.forEach((block) => {
        if (block.dataset.applyTo === 'all') {
          panelContents.forEach((content, i) => {
            const isLast = i === panelContents.length - 1;
            this.#moveChildren(isLast ? block : block.cloneNode(true), content);
          });
          return;
        }

        const targets = (block.dataset.menuIndex || '')
          .split(',')
          .map((s) => parseInt(s.trim(), 10))
          .filter((n) => Number.isFinite(n))
          .map((n) => this.querySelector(`[data-megamenu-index="${n}"] [data-megamenu-content]`))
          .filter(Boolean);

        // The source container is removed below, so an index that matches no
        // menu item silently drops the merchant's panel content. Say so, or a
        // stale index just looks like an empty megamenu.
        if (!targets.length) {
          console.warn(
            `apc-header: megamenu panel targets menu index "${block.dataset.menuIndex}", which matches no menu item with a dropdown.`,
          );
        }

        targets.forEach((target, i) => {
          const isLast = i === targets.length - 1;
          this.#moveChildren(isLast ? block : block.cloneNode(true), target);
        });
      });

      source.remove();
    });
  }

  /**
   * @param {AbortSignal} signal
   */
  #setupMegamenu(signal) {
    const triggers = this.querySelectorAll('[data-mega-trigger]');
    const panels = this.querySelectorAll('[data-mega-panel]');
    const backdrop = this.querySelector('[data-megamenu-backdrop]');
    if (!triggers.length || !panels.length) return;

    /** @param {Element | null} root */
    const focusables = (root) =>
      root
        ? [
            ...root.querySelectorAll('a[href], button:not([disabled]), input, select, [tabindex]:not([tabindex="-1"])'),
          ].filter((el) => el.checkVisibility?.() ?? true)
        : [];
    /** @param {string} handle */
    const panelFor = (handle) => this.querySelector(`[data-mega-panel="${handle}"]`);
    /** @param {string} handle */
    const disclosureFor = (handle) =>
      /** @type {HTMLElement | null} */ (this.querySelector(`[data-mega-disclosure="${handle}"]`));

    const cancelClose = () => {
      if (this.#closeTimer) {
        clearTimeout(this.#closeTimer);
        this.#closeTimer = null;
      }
    };

    /** @param {string | null} handle */
    const setPanelOpen = (handle) => {
      panels.forEach((panel) => {
        const isActive = panel.dataset.megaPanel === handle;
        panel.inert = !isActive;
        if (isActive) {
          panel.setAttribute('data-open', 'true');
          panel.setAttribute('aria-hidden', 'false');
        } else {
          panel.removeAttribute('data-open');
          panel.setAttribute('aria-hidden', 'true');
        }
      });
      triggers.forEach((trigger) => {
        const isActive = trigger.dataset.megaTrigger === handle;
        trigger.closest('.apc-navbar__item')?.toggleAttribute('data-mega-active', isActive);
        disclosureFor(trigger.dataset.megaTrigger)?.setAttribute('aria-expanded', String(isActive));
      });
    };

    /** @param {string} handle */
    const openMega = (handle) => {
      cancelClose();
      if (this.#activeHandle === handle) return;
      this.#activeHandle = handle;
      setPanelOpen(handle);
      this.setAttribute('data-mega-open', 'true');
    };

    /** @param {boolean} [immediate] */
    const closeMega = (immediate = false) => {
      const run = () => {
        // Don't close a panel the keyboard is inside; that leaves the focus ring nowhere
        if (!immediate && [...panels].some((panel) => panel.contains(document.activeElement))) return;
        this.#activeHandle = null;
        setPanelOpen(null);
        this.removeAttribute('data-mega-open');
      };
      cancelClose();
      if (immediate) {
        run();
      } else {
        this.#closeTimer = setTimeout(run, CLOSE_DELAY);
      }
    };

    triggers.forEach((trigger) => {
      const handle = trigger.dataset.megaTrigger;
      const item = trigger.closest('.apc-navbar__item');
      trigger.addEventListener('mouseenter', () => openMega(handle), { signal });
      trigger.addEventListener('mouseleave', () => closeMega(), { signal });
      // Tabbing to another item closes what the keyboard opened; focus alone never opens a panel
      item?.addEventListener(
        'focusin',
        () => {
          if (this.#activeHandle && this.#activeHandle !== handle) closeMega(true);
        },
        { signal },
      );
      // click, not keydown: a <button> fires it for both Enter and Space
      disclosureFor(handle)?.addEventListener(
        'click',
        () => {
          if (this.#activeHandle === handle) return closeMega(true);
          openMega(handle);
          focusables(panelFor(handle))[0]?.focus();
        },
        { signal },
      );
    });

    panels.forEach((panel) => {
      panel.inert = true;
      panel.addEventListener('mouseenter', cancelClose, { signal });
      panel.addEventListener('mouseleave', () => closeMega(), { signal });
      // Tab off either end of the panel hands focus back to its disclosure button and closes it
      panel.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Tab') return;
          const items = focusables(panel);
          const edge = event.shiftKey ? items[0] : items[items.length - 1];
          if (event.target !== edge) return;
          event.preventDefault();
          disclosureFor(panel.dataset.megaPanel)?.focus();
          closeMega(true);
        },
        { signal },
      );
    });

    backdrop?.addEventListener('click', () => closeMega(true), { signal });

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.#activeHandle) {
          const handle = this.#activeHandle;
          const wasInside = panelFor(handle)?.contains(document.activeElement);
          closeMega(true);
          if (wasInside) disclosureFor(handle)?.focus();
        }
      },
      { signal },
    );
  }

  /**
   * @param {AbortSignal} signal
   */
  #setupScrollCollapse(signal) {
    this.#scrollLastY = window.scrollY;
    window.addEventListener('scroll', this.#onScroll, { passive: true, signal });
  }

  #onScroll = () => {
    if (this.#scrollTicking) return;
    requestAnimationFrame(this.#updateScroll);
    this.#scrollTicking = true;
  };

  #updateScroll = () => {
    const y = window.scrollY;
    const delta = y - this.#scrollLastY;

    if (delta > 0) {
      this.#scrollDownAccum += delta;
      this.#scrollUpAccum = 0;
    } else if (delta < 0) {
      this.#scrollUpAccum += -delta;
      this.#scrollDownAccum = 0;
    }

    const isHideMode = this.hasAttribute('data-hide-on-scroll');
    const stateAttr = isHideMode ? 'data-hidden' : 'data-collapsed';
    // In hide mode, scale trigger distances to the header's own height —
    // a full header for hiding (deliberate), a fraction for revealing (snappy).
    const headerHeight = this.offsetHeight || SCROLL_TRIGGER_DISTANCE;
    const downTrigger = isHideMode ? headerHeight : SCROLL_TRIGGER_DISTANCE;
    const upTrigger = isHideMode ? headerHeight / 3 : SCROLL_TRIGGER_DISTANCE;

    if (y < SCROLL_TOP_THRESHOLD) {
      this.removeAttribute(stateAttr);
      this.#scrollDownAccum = 0;
      this.#scrollUpAccum = 0;
    } else if (!this.hasAttribute('data-mega-open')) {
      if (this.#scrollDownAccum > downTrigger && !this.hasAttribute(stateAttr)) {
        this.setAttribute(stateAttr, 'true');
        this.#scrollDownAccum = 0;
      } else if (this.#scrollUpAccum > upTrigger && this.hasAttribute(stateAttr)) {
        this.removeAttribute(stateAttr);
        this.#scrollUpAccum = 0;
      }
    }

    this.#scrollLastY = y;
    this.#scrollTicking = false;
  };
}

if (!customElements.get('apc-header')) {
  customElements.define('apc-header', ApcHeader);
}
